import '@apps/web/routes/_app/admin/agent/theme.css';

import { createFileRoute, Link } from '@tanstack/react-router';
import {
    ArrowLeft,
    CircleCheck,
    Clock3,
    Database,
    History,
    Plus,
    RefreshCw,
    RotateCcw,
} from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { RenderFrame } from '@apps/web/lib/render_frame/render_frame';
import { buildFrameSourceDocument } from '@apps/web/lib/render_frame/source_document';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { type ApiResponseType } from '@libraries/typescript/api_client';

// Parse totally so a hand-edited URL cannot throw into the router error
// boundary, but keep “present but unusable” distinguishable from “absent”:
// this page fails closed on a bad pin rather than silently rendering whatever
// pair happens to be active. `.catch('')` — not `.catch(undefined)` — is what
// keeps `?versionId=` from collapsing into the unpinned case.
const pinnedPairSearchSchema = z.object({
    versionId: z.string().optional().catch(''),
    snapshotId: z.string().optional().catch(''),
});

const pinnedPairSchema = z.object({
    versionId: z.string().uuid(),
    snapshotId: z.string().uuid(),
});

export const Route = createFileRoute('/_app/admin/agent/pages/$slug')({
    component: AdminAgentPageDetail,
    validateSearch: pinnedPairSearchSchema,
    head: () => ({
        meta: [{ title: 'Agent Data Page | Phoebe Admin' }],
    }),
});

type SelectedPair = {
    readonly versionId: string;
    readonly snapshotId: string;
};

type PagePin =
    | { readonly kind: 'follow_active' }
    | { readonly kind: 'malformed' }
    | { readonly kind: 'pinned'; readonly pair: SelectedPair };

type PageSelection =
    | { readonly status: 'resolved'; readonly pair: SelectedPair }
    | { readonly status: 'malformed_pin' }
    | { readonly status: 'unresolved_pin' }
    | { readonly status: 'empty' };

function readPin(search: z.infer<typeof pinnedPairSearchSchema>): PagePin {
    if (search.versionId === undefined && search.snapshotId === undefined) {
        return { kind: 'follow_active' };
    }
    const parsed = pinnedPairSchema.safeParse(search);
    // A half-written pair is a broken link, not a snapshot we dropped.
    return parsed.success
        ? { kind: 'pinned', pair: parsed.data }
        : { kind: 'malformed' };
}

type Page = ApiResponseType<'/admin/agent/pages/{slug}', 'get'>;
type Version = NonNullable<Page['versions']>[number];
type Snapshot = NonNullable<Version['snapshots']>[number];

const REFRESH_SCHEDULE_OPTIONS = [
    { value: 'manual', label: 'Manual only' },
    { value: '5', label: 'Every 5 minutes' },
    { value: '15', label: 'Every 15 minutes' },
    { value: '60', label: 'Hourly' },
    { value: '1440', label: 'Daily' },
    { value: '10080', label: 'Weekly' },
] as const;

const VIEW_REFRESH_MAX_AGE_OPTIONS = [
    { value: 'disabled', label: 'Do not refresh when opened' },
    { value: '5', label: 'Refresh if 5 minutes old' },
    { value: '15', label: 'Refresh if 15 minutes old' },
    { value: '60', label: 'Refresh if 1 hour old' },
    { value: '1440', label: 'Refresh if 1 day old' },
] as const;

function AdminAgentPageDetail() {
    const { slug } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const apiClient = getApiClient();
    const [isActivating, setIsActivating] = useState(false);
    const [isCreatingRevision, setIsCreatingRevision] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSavingSchedule, setIsSavingSchedule] = useState(false);
    const [refreshFailure, setRefreshFailure] = useState<string | null>(null);
    const [scheduleSelection, setScheduleSelection] = useState('manual');
    const [viewMaxAgeSelection, setViewMaxAgeSelection] = useState('disabled');
    const {
        data: page,
        error,
        isLoading,
        mutate,
    } = useApiQuery({
        path: '/admin/agent/pages/{slug}',
        method: 'get',
        pathParams: { slug },
        select: (response) => response,
    });

    const pin = useMemo(() => readPin(search), [search]);
    const selection = useMemo((): PageSelection => {
        if (pin.kind === 'malformed') {
            return { status: 'malformed_pin' };
        }
        if (!page) {
            return { status: 'empty' };
        }
        if (pin.kind === 'pinned') {
            const version = page.versions?.find(
                (candidate) => candidate.id === pin.pair.versionId,
            );
            return version?.snapshots?.some(
                (snapshot) => snapshot.id === pin.pair.snapshotId,
            )
                ? { status: 'resolved', pair: pin.pair }
                : { status: 'unresolved_pin' };
        }
        if (page.active_version_id && page.active_snapshot_id) {
            return {
                status: 'resolved',
                pair: {
                    versionId: page.active_version_id,
                    snapshotId: page.active_snapshot_id,
                },
            };
        }
        const latestVersion = page.versions?.[0];
        const latestSnapshot = latestVersion?.snapshots?.[0];
        return latestVersion && latestSnapshot
            ? {
                  status: 'resolved',
                  pair: {
                      versionId: latestVersion.id,
                      snapshotId: latestSnapshot.id,
                  },
              }
            : { status: 'empty' };
    }, [page, pin]);
    const selectedPair =
        selection.status === 'resolved' ? selection.pair : null;

    const pinPair = (pair: SelectedPair) => {
        void navigate({
            search: {
                versionId: pair.versionId,
                snapshotId: pair.snapshotId,
            },
            replace: true,
        });
    };

    useEffect(() => {
        setScheduleSelection(
            page?.refresh_schedule?.status === 'active'
                ? String(page.refresh_schedule.interval_minutes)
                : 'manual',
        );
    }, [
        page?.refresh_schedule?.interval_minutes,
        page?.refresh_schedule?.status,
    ]);

    useEffect(() => {
        setViewMaxAgeSelection(
            page?.refresh_schedule?.refresh_on_view_max_age_minutes
                ? String(page.refresh_schedule.refresh_on_view_max_age_minutes)
                : 'disabled',
        );
    }, [page?.refresh_schedule?.refresh_on_view_max_age_minutes]);

    useEffect(() => {
        if (
            !page?.refresh_schedule?.view_refresh_pending &&
            !page?.refresh_schedule?.refresh_in_progress
        ) {
            return;
        }
        const poll = window.setInterval(() => void mutate(), 2_000);
        return () => window.clearInterval(poll);
    }, [
        mutate,
        page?.refresh_schedule?.refresh_in_progress,
        page?.refresh_schedule?.view_refresh_pending,
    ]);

    const { data: content, error: contentError } = useApiQuery({
        path: '/admin/agent/pages/{slug}/versions/{version_id}/snapshots/{snapshot_id}/content',
        method: 'get',
        pathParams: {
            slug,
            version_id: selectedPair?.versionId ?? '',
            snapshot_id: selectedPair?.snapshotId ?? '',
        },
        enabled: selectedPair !== null,
        select: (response) => response,
    });

    const selectedVersion = page?.versions?.find(
        (version) => version.id === selectedPair?.versionId,
    );
    const selectedSnapshot = selectedVersion?.snapshots?.find(
        (snapshot) => snapshot.id === selectedPair?.snapshotId,
    );
    const isSelectedActive =
        page?.active_version_id === selectedPair?.versionId &&
        page?.active_snapshot_id === selectedPair?.snapshotId;

    const activateSelected = async () => {
        if (!page || !selectedPair) {
            return;
        }
        setIsActivating(true);
        try {
            await apiClient.post({
                url: '/admin/agent/pages/{slug}/versions/{version_id}/activate',
                pathParams: {
                    slug,
                    version_id: selectedPair.versionId,
                },
                body: {
                    snapshot_id: selectedPair.snapshotId,
                    expected_active_version_id: page.active_version_id,
                    expected_active_snapshot_id: page.active_snapshot_id,
                    client_operation_id: crypto.randomUUID(),
                },
            });
            await mutate();
            toast.success('Published exact page version and snapshot', {
                persistNotification: false,
            });
        } catch (activationError) {
            toast.error(
                activationError instanceof Error
                    ? activationError.message
                    : 'Could not activate page',
                { persistNotification: false },
            );
        } finally {
            setIsActivating(false);
        }
    };

    const refreshActive = async () => {
        if (
            !page?.active_version_id ||
            !page.active_snapshot_id ||
            isRefreshing
        ) {
            return;
        }
        setIsRefreshing(true);
        setRefreshFailure(null);
        try {
            const result = await apiClient.post({
                url: '/admin/agent/pages/{slug}/refresh',
                pathParams: { slug },
                body: {
                    expected_active_version_id: page.active_version_id,
                    expected_active_snapshot_id: page.active_snapshot_id,
                    client_operation_id: crypto.randomUUID(),
                },
            });
            await mutate();
            if (result.outcome === 'failed') {
                setRefreshFailure(
                    result.error_code ?? 'The dataset refresh failed.',
                );
                return;
            }
            if (result.snapshot_id && pin.kind === 'pinned') {
                pinPair({
                    versionId: result.version_id,
                    snapshotId: result.snapshot_id,
                });
            }
            toast.success(
                result.outcome === 'activated'
                    ? 'Data refreshed without changing page code'
                    : 'Refresh completed but lost the active-page race',
                { persistNotification: false },
            );
        } catch (refreshError) {
            setRefreshFailure(
                refreshError instanceof Error
                    ? refreshError.message
                    : 'The dataset refresh failed.',
            );
        } finally {
            setIsRefreshing(false);
        }
    };

    const createRevisedDraft = async () => {
        if (isCreatingRevision) {
            return;
        }
        setIsCreatingRevision(true);
        try {
            const revisedPage = await apiClient.post({
                url: '/admin/agent/pages/fixtures/voice-quality',
                body: {
                    client_operation_id: crypto.randomUUID(),
                    fixture_revision: 2,
                    layout_variant: 2,
                    runtime_variant: 'worker',
                },
            });
            await mutate();
            const revisedVersion = revisedPage.versions?.[0];
            const revisedSnapshot = revisedVersion?.snapshots?.[0];
            if (revisedVersion && revisedSnapshot) {
                pinPair({
                    versionId: revisedVersion.id,
                    snapshotId: revisedSnapshot.id,
                });
            }
            toast.success('Created a revised inactive presentation version', {
                persistNotification: false,
            });
        } catch (revisionError) {
            toast.error(
                revisionError instanceof Error
                    ? revisionError.message
                    : 'Could not create revised draft',
                { persistNotification: false },
            );
        } finally {
            setIsCreatingRevision(false);
        }
    };

    const saveRefreshSchedule = async () => {
        if (!page || isSavingSchedule) {
            return;
        }
        const enabled = scheduleSelection !== 'manual';
        const intervalMinutes = enabled
            ? Number(scheduleSelection)
            : (page.refresh_schedule?.interval_minutes ?? 60);
        const refreshOnViewMaxAgeMinutes =
            viewMaxAgeSelection === 'disabled'
                ? null
                : Number(viewMaxAgeSelection);
        setIsSavingSchedule(true);
        try {
            await apiClient.post({
                url: '/admin/agent/pages/{slug}/refresh-schedule',
                pathParams: { slug },
                body: {
                    enabled,
                    interval_minutes: intervalMinutes,
                    expected_revision: page.refresh_schedule?.revision ?? null,
                    refresh_on_view_max_age_minutes: refreshOnViewMaxAgeMinutes,
                },
            });
            await mutate();
            toast.success(
                enabled
                    ? 'Automatic data refresh scheduled'
                    : 'Automatic data refresh paused',
                { persistNotification: false },
            );
        } catch (scheduleError) {
            toast.error(
                scheduleError instanceof Error
                    ? scheduleError.message
                    : 'Could not update refresh schedule',
                { persistNotification: false },
            );
        } finally {
            setIsSavingSchedule(false);
        }
    };

    if (selection.status === 'malformed_pin') {
        return (
            <PageShell testId="page-pin-malformed">
                <span>
                    This link’s page version and snapshot parameters are
                    malformed. A page link must carry both a valid version ID
                    and a valid snapshot ID.{' '}
                    <Link
                        to="/admin/agent/pages/$slug"
                        params={{ slug }}
                        search={{}}
                        className="text-foreground underline"
                    >
                        Open the current active page.
                    </Link>
                </span>
            </PageShell>
        );
    }
    if (isLoading) {
        return <PageShell>Loading data page…</PageShell>;
    }
    if (error || !page) {
        return <PageShell>Data page could not be loaded.</PageShell>;
    }
    if (selection.status === 'unresolved_pin') {
        return (
            <PageShell testId="page-pin-unresolved">
                <span>
                    The requested immutable page version and snapshot are not
                    available in the retained page history.{' '}
                    <Link
                        to="/admin/agent/pages/$slug"
                        params={{ slug }}
                        search={{}}
                        className="text-foreground underline"
                    >
                        Open the current active page.
                    </Link>
                </span>
            </PageShell>
        );
    }

    return (
        <main className="admin-agent-page bg-background min-h-svh p-4 md:p-6">
            <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
                <header className="flex flex-wrap items-start justify-between gap-4 border-b pb-4">
                    <div>
                        <Link
                            to="/admin/agent/pages"
                            className="text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1 text-xs"
                        >
                            <ArrowLeft className="size-3" />
                            Data pages
                        </Link>
                        <h1 className="text-2xl font-semibold">{page.title}</h1>
                        <p className="text-muted-foreground mt-1 text-sm">
                            /admin/agent/pages/{page.slug}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {getRunEnvironment() !== 'production' &&
                        slug === 'voice-quality' ? (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => void createRevisedDraft()}
                                disabled={isCreatingRevision}
                                data-testid="create-revised-page-fixture"
                            >
                                <Plus className="size-4" />
                                {isCreatingRevision
                                    ? 'Creating…'
                                    : 'Create revised draft'}
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => void refreshActive()}
                            disabled={
                                !page.active_version_id ||
                                !page.active_snapshot_id ||
                                isRefreshing
                            }
                            data-testid="refresh-page-data"
                        >
                            <RefreshCw
                                className={
                                    isRefreshing
                                        ? 'size-4 animate-spin'
                                        : 'size-4'
                                }
                            />
                            Refresh data
                        </Button>
                        <Button
                            type="button"
                            onClick={() => void activateSelected()}
                            disabled={
                                !selectedPair ||
                                isSelectedActive ||
                                isActivating
                            }
                            data-testid="activate-page-pair"
                        >
                            {selectedVersion &&
                            page.active_version_id &&
                            selectedVersion.version <
                                (page.versions?.find(
                                    (version) =>
                                        version.id === page.active_version_id,
                                )?.version ?? 0) ? (
                                <RotateCcw className="size-4" />
                            ) : (
                                <CircleCheck className="size-4" />
                            )}
                            {isSelectedActive ? 'Active' : 'Activate pair'}
                        </Button>
                    </div>
                </header>
                {refreshFailure ? (
                    <div
                        className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border p-3 text-sm"
                        data-testid="refresh-failure"
                    >
                        Refresh failed ({refreshFailure}). The prior good
                        snapshot remains active.
                    </div>
                ) : null}
                <ViewRefreshNotice page={page} />
                <section className="grid min-h-[680px] gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
                    <div className="dark:bg-card overflow-hidden rounded-md border bg-white">
                        {contentError ? (
                            <div className="text-destructive flex h-full items-center justify-center p-8 text-sm">
                                Verified page content could not be loaded.
                            </div>
                        ) : content ? (
                            <AdminPageFrame
                                body={content.body}
                                presentationRuntime={
                                    content.presentation_runtime
                                }
                                title={page.title}
                            />
                        ) : (
                            <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-sm">
                                Loading verified page snapshot…
                            </div>
                        )}
                    </div>
                    <aside className="flex min-h-0 flex-col gap-3 overflow-auto">
                        <MetadataPanel
                            version={selectedVersion}
                            snapshot={selectedSnapshot}
                            isActive={Boolean(isSelectedActive)}
                        />
                        <RefreshSchedulePanel
                            schedule={page.refresh_schedule}
                            selection={scheduleSelection}
                            viewMaxAgeSelection={viewMaxAgeSelection}
                            onSelectionChange={setScheduleSelection}
                            onViewMaxAgeSelectionChange={setViewMaxAgeSelection}
                            onSave={() => void saveRefreshSchedule()}
                            isSaving={isSavingSchedule}
                        />
                        <HistoryPanel
                            page={page}
                            selectedPair={selectedPair}
                            onSelect={pinPair}
                        />
                    </aside>
                </section>
            </div>
        </main>
    );
}

function ViewRefreshNotice({ page }: { readonly page: Page }) {
    const schedule = page.refresh_schedule;
    const maxAgeMinutes = schedule?.refresh_on_view_max_age_minutes;
    if (
        schedule?.status !== 'active' ||
        !maxAgeMinutes ||
        !page.active_version_id ||
        !page.active_snapshot_id
    ) {
        return null;
    }
    const refreshUnderway =
        schedule.view_refresh_pending || schedule.refresh_in_progress;
    const activeVersion = page.versions?.find(
        (version) => version.id === page.active_version_id,
    );
    const activeSnapshot = activeVersion?.snapshots?.find(
        (snapshot) => snapshot.id === page.active_snapshot_id,
    );
    if (
        !refreshUnderway &&
        (!activeSnapshot ||
            Date.parse(activeSnapshot.data_as_of) >
                Date.now() - maxAgeMinutes * 60_000)
    ) {
        return null;
    }
    const failed =
        schedule.last_trigger_kind === 'view' &&
        (schedule.last_outcome === 'failed' ||
            schedule.last_outcome === 'unexpected_error');
    return (
        <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
            data-testid="view-refresh-notice"
        >
            {refreshUnderway
                ? 'Showing the last verified snapshot while a fresh copy is prepared.'
                : failed
                  ? `Showing the last verified snapshot. The latest refresh-on-open attempt failed${
                        schedule.last_error_code
                            ? ` (${schedule.last_error_code})`
                            : ''
                    }.`
                  : 'Showing the last verified snapshot until refresh-on-open may retry.'}
        </div>
    );
}

function RefreshSchedulePanel({
    schedule,
    selection,
    viewMaxAgeSelection,
    onSelectionChange,
    onViewMaxAgeSelectionChange,
    onSave,
    isSaving,
}: {
    readonly schedule: Page['refresh_schedule'];
    readonly selection: string;
    readonly viewMaxAgeSelection: string;
    readonly onSelectionChange: (value: string) => void;
    readonly onViewMaxAgeSelectionChange: (value: string) => void;
    readonly onSave: () => void;
    readonly isSaving: boolean;
}) {
    const storedSelection =
        schedule?.status === 'active'
            ? String(schedule.interval_minutes)
            : 'manual';
    const storedViewMaxAgeSelection =
        schedule?.refresh_on_view_max_age_minutes !== null &&
        schedule?.refresh_on_view_max_age_minutes !== undefined
            ? String(schedule.refresh_on_view_max_age_minutes)
            : 'disabled';
    const hasChanges =
        selection !== storedSelection ||
        viewMaxAgeSelection !== storedViewMaxAgeSelection;
    const knownOption = REFRESH_SCHEDULE_OPTIONS.some(
        (option) => option.value === selection,
    );
    return (
        <section
            className="rounded-md border p-4"
            data-testid="refresh-schedule-panel"
        >
            <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Clock3 className="size-4" />
                Data refresh
            </h2>
            <p className="text-muted-foreground mt-1 text-xs">
                Reruns only the dataset contract pinned by the active
                presentation.
            </p>
            <div className="mt-3 flex gap-2">
                <label className="sr-only" htmlFor="admin-page-refresh-cadence">
                    Refresh cadence
                </label>
                <select
                    id="admin-page-refresh-cadence"
                    className="border-input bg-background h-9 min-w-0 flex-1 rounded-md border px-3 text-sm"
                    value={selection}
                    onChange={(event) =>
                        onSelectionChange(event.currentTarget.value)
                    }
                    data-testid="refresh-schedule-cadence"
                >
                    {!knownOption ? (
                        <option value={selection}>
                            Every {selection} minutes
                        </option>
                    ) : null}
                    {REFRESH_SCHEDULE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <Button
                    type="button"
                    variant="outline"
                    onClick={onSave}
                    disabled={!hasChanges || isSaving}
                    data-testid="save-refresh-schedule"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </Button>
            </div>
            <div className="mt-2">
                <label className="sr-only" htmlFor="admin-page-refresh-on-view">
                    Refresh stale data when opened
                </label>
                <select
                    id="admin-page-refresh-on-view"
                    className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                    value={viewMaxAgeSelection}
                    onChange={(event) =>
                        onViewMaxAgeSelectionChange(event.currentTarget.value)
                    }
                    disabled={selection === 'manual'}
                    data-testid="refresh-on-view-max-age"
                >
                    {VIEW_REFRESH_MAX_AGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <dl className="mt-3 grid grid-cols-[92px_minmax(0,1fr)] gap-2 border-t pt-3 text-xs">
                <dt className="text-muted-foreground">Status</dt>
                <dd data-testid="refresh-schedule-status">
                    {schedule?.status === 'active'
                        ? schedule.refresh_in_progress
                            ? 'Refreshing'
                            : 'Scheduled'
                        : 'Manual only'}
                </dd>
                {schedule?.status === 'active' ? (
                    <Fragment>
                        <dt className="text-muted-foreground">Next due</dt>
                        <dd>{formatTimestamp(schedule.next_refresh_at)}</dd>
                    </Fragment>
                ) : null}
                <dt className="text-muted-foreground">Last completed</dt>
                <dd>
                    {schedule?.last_completed_at
                        ? formatTimestamp(schedule.last_completed_at)
                        : '—'}
                </dd>
                <dt className="text-muted-foreground">Last outcome</dt>
                <dd data-testid="refresh-schedule-last-outcome">
                    {schedule?.last_outcome ?? '—'}
                </dd>
                <dt className="text-muted-foreground">Last trigger</dt>
                <dd>{schedule?.last_trigger_kind ?? '—'}</dd>
                {schedule?.consecutive_failures ? (
                    <Fragment>
                        <dt className="text-muted-foreground">Failures</dt>
                        <dd className="text-destructive">
                            {schedule.consecutive_failures}
                            {schedule.last_error_code
                                ? ` · ${schedule.last_error_code}`
                                : ''}
                        </dd>
                    </Fragment>
                ) : null}
            </dl>
        </section>
    );
}

function AdminPageFrame({
    body,
    presentationRuntime,
    title,
}: {
    readonly body: string;
    readonly presentationRuntime:
        | 'component_app_v1'
        | 'sandboxed_javascript_v1'
        | 'static_html_v1';
    readonly title: string;
}) {
    const sourceDocument = useMemo(
        () =>
            buildFrameSourceDocument({
                body,
                presentationRuntime,
            }).sourceDocument,
        [body, presentationRuntime],
    );
    return (
        <RenderFrame
            title={title}
            sourceDocument={sourceDocument}
            scripts={presentationRuntime !== 'static_html_v1'}
            className="h-full min-h-[680px] w-full border-0"
            testId="admin-page-frame"
        />
    );
}

function MetadataPanel({
    version,
    snapshot,
    isActive,
}: {
    readonly version: Version | undefined;
    readonly snapshot: Snapshot | undefined;
    readonly isActive: boolean;
}) {
    if (!version || !snapshot) {
        return null;
    }
    return (
        <section className="rounded-md border p-4">
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">Selected pair</h2>
                <span className="text-muted-foreground text-xs">
                    {isActive ? 'Active' : 'Historical / draft'}
                </span>
            </div>
            <dl className="mt-3 grid grid-cols-[108px_minmax(0,1fr)] gap-2 text-xs">
                <dt className="text-muted-foreground">Presentation</dt>
                <dd>v{version.version}</dd>
                <dt className="text-muted-foreground">Snapshot</dt>
                <dd>#{snapshot.refresh_sequence}</dd>
                <dt className="text-muted-foreground">Data as of</dt>
                <dd>{formatTimestamp(snapshot.data_as_of)}</dd>
                <dt className="text-muted-foreground">Dataset</dt>
                <dd>
                    {snapshot.dataset_contract_name} v
                    {snapshot.dataset_contract_version}
                </dd>
                <dt className="text-muted-foreground">Source</dt>
                <dd>
                    {String(
                        snapshot.source['label'] ?? snapshot.source['kind'],
                    )}
                </dd>
                <dt className="text-muted-foreground">Timezone</dt>
                <dd>{snapshot.timezone}</dd>
                <dt className="text-muted-foreground">Rows</dt>
                <dd>{snapshot.row_count}</dd>
                <dt className="text-muted-foreground">Body digest</dt>
                <dd className="truncate font-mono" title={snapshot.body_sha256}>
                    {snapshot.body_sha256.slice(0, 12)}
                </dd>
            </dl>
            <div className="mt-3 border-t pt-3">
                <p className="text-muted-foreground text-xs font-medium">
                    Caveats
                </p>
                <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-4 text-xs">
                    {snapshot.caveats.map((caveat) => (
                        <li key={caveat}>{caveat}</li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function HistoryPanel({
    page,
    selectedPair,
    onSelect,
}: {
    readonly page: Page;
    readonly selectedPair: SelectedPair | null;
    readonly onSelect: (pair: SelectedPair) => void;
}) {
    return (
        <section className="rounded-md border p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
                <History className="size-4" />
                Immutable history
            </h2>
            <div className="mt-3 space-y-4">
                {(page.versions ?? []).map((version) => (
                    <div
                        key={version.id}
                        data-testid={`admin-page-version-${version.id}`}
                    >
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold">
                                Presentation v{version.version}
                            </span>
                            <span className="text-muted-foreground font-mono">
                                {version.template_sha256.slice(0, 8)}
                            </span>
                        </div>
                        <div className="mt-2 space-y-1">
                            {(version.snapshots ?? []).map((snapshot) => {
                                const selected =
                                    version.id === selectedPair?.versionId &&
                                    snapshot.id === selectedPair.snapshotId;
                                const active =
                                    version.id === page.active_version_id &&
                                    snapshot.id === page.active_snapshot_id;
                                return (
                                    <button
                                        type="button"
                                        key={snapshot.id}
                                        onClick={() =>
                                            onSelect({
                                                versionId: version.id,
                                                snapshotId: snapshot.id,
                                            })
                                        }
                                        className={
                                            'flex w-full items-center gap-2 rounded border p-2 text-left text-xs ' +
                                            (selected
                                                ? 'border-foreground bg-muted'
                                                : 'hover:border-border hover:bg-muted/50 border-transparent')
                                        }
                                        data-testid={`admin-page-snapshot-${snapshot.id}`}
                                    >
                                        <Database className="size-3.5 shrink-0" />
                                        <span className="min-w-0 flex-1">
                                            Snapshot #
                                            {snapshot.refresh_sequence}
                                            <span className="text-muted-foreground mt-0.5 flex items-center gap-1">
                                                <Clock3 className="size-3" />
                                                {formatTimestamp(
                                                    snapshot.data_as_of,
                                                )}
                                            </span>
                                        </span>
                                        {active ? (
                                            <span className="text-emerald-700 dark:text-emerald-300">
                                                active
                                            </span>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function formatTimestamp(value: string) {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
}

function PageShell({
    children,
    testId,
}: {
    readonly children: ReactNode;
    readonly testId?: string;
}) {
    return (
        <main
            className="text-muted-foreground flex min-h-svh items-center justify-center p-8 text-sm"
            data-testid={testId}
        >
            {children}
        </main>
    );
}
